import { BroadcastManyQuery, EnumBroadcastResourcesType } from "gql/generated";
import i18n, { i18nNamespace, loadLanguagePacks } from "i18n";
import { z } from "zod";
import { useCallback, useMemo } from "react";
import { useFormHandler, usePostHog } from "shared/hooks";
import { getMultilingualString } from "shared/utils";

// Ensure the required language packs are loaded
await loadLanguagePacks([i18nNamespace.Core, i18nNamespace.Home]);

export const createEditResourceFormSchema = z
  .object({
    type: z.nativeEnum(EnumBroadcastResourcesType),
    title: z
      .string({
        required_error: `${i18n.t("home:VIEWS.BROADCAST.MODAL.RESOURCES.FORM.TITLE")} ${i18n.t("core:FORM_ERRORS.REQUIRED")}`,
      })
      .min(1, {
        message: `${i18n.t("home:VIEWS.BROADCAST.MODAL.RESOURCES.FORM.TITLE")} ${i18n.t("core:FORM_ERRORS.REQUIRED")}`,
      }),
    description: z.string().optional(),
    coverImage: z.string().optional(),
    message: z.string().optional(),
    file: z.string().optional(),
    video: z.string().optional(),
    videoURL: z.string().optional(),
    photos: z.array(z.string()).optional(),
    course: z.string().optional(),
    iframeUrl: z.string().optional(),
    survey: z.string().optional(),
    formMode: z.string().optional(),
    allowSaveAndEdit: z.boolean().optional(),
    requireApproval: z.boolean().optional(),
    allowResubmission: z.boolean().optional(),
    completionTags: z.array(z.string()).optional(),
    submissionTags: z.array(z.string()).optional(),
    booker: z.string().optional(),
    teamVisible: z.boolean().optional(),
    ctaLabel: z.string().optional(),
    _id: z.string(),
  })
  .superRefine((values, ctx) => {
    switch (values.type) {
      case EnumBroadcastResourcesType.Message:
        if (!values.message || values.message.length === 0) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["message"],
            message:
              i18n.t("home:VIEWS.BROADCAST.RESOURCE_TYPE.MESSAGE") +
              " " +
              i18n.t("core:FORM_ERRORS.REQUIRED"),
          });
        }
        break;
      case EnumBroadcastResourcesType.Pdf:
        if (!values.file || values.file.length === 0) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["file"],
            message:
              i18n.t("home:VIEWS.BROADCAST.RESOURCE_TYPE.PDF") +
              " " +
              i18n.t("core:FORM_ERRORS.REQUIRED"),
          });
        }
        break;
      case EnumBroadcastResourcesType.Video:
        if (!values.video && !values.videoURL) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["video"],
            message:
              i18n.t("home:VIEWS.BROADCAST.RESOURCE_TYPE.VIDEO") +
              " " +
              i18n.t("core:FORM_ERRORS.REQUIRED"),
          });
        }
        break;
      case EnumBroadcastResourcesType.Photos:
        if (!values.photos || values.photos.length === 0) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["photos"],
            message:
              i18n.t("home:VIEWS.BROADCAST.RESOURCE_TYPE.PHOTOS") +
              " " +
              i18n.t("core:FORM_ERRORS.REQUIRED"),
          });
        }
        break;
      case EnumBroadcastResourcesType.Course:
        if (!values.course || values.course.length === 0) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["course"],
            message:
              i18n.t("home:VIEWS.BROADCAST.RESOURCE_TYPE.COURSE") +
              " " +
              i18n.t("core:FORM_ERRORS.REQUIRED"),
          });
        }
        break;
      case EnumBroadcastResourcesType.Forms:
        if (!values.survey || values.survey.length === 0) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["survey"],
            message:
              i18n.t("home:VIEWS.BROADCAST.RESOURCE_TYPE.FORMS") +
              " " +
              i18n.t("core:FORM_ERRORS.REQUIRED"),
          });
        }
        break;
      case EnumBroadcastResourcesType.Storefront:
        if (!values.booker || values.booker.length === 0) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["booker"],
            message:
              i18n.t("home:VIEWS.BROADCAST.RESOURCE_TYPE.STOREFRONT") +
              " " +
              i18n.t("core:FORM_ERRORS.REQUIRED"),
          });
        }
        break;
      // Handle CUSTOM pre/post codegen by string comparison
      case undefined:
      default:
        if (values.type === EnumBroadcastResourcesType.Custom) {
          if (!values.iframeUrl || values.iframeUrl.length === 0) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: ["iframeUrl"],
              message:
                i18n.t("home:VIEWS.BROADCAST.RESOURCE_TYPE.CUSTOM") +
                " " +
                i18n.t("core:FORM_ERRORS.REQUIRED"),
            });
          }
        }
    }
  });

export type CreateEditResourceFormValues = z.infer<
  typeof createEditResourceFormSchema
>;

interface CreateEditResourceFormOptions {
  onSuccess?: (resource: CreateEditResourceFormValues) => void;
  onError?: (message: string) => void;
  resource?: BroadcastManyQuery["broadcastMany"][0]["resources"][0];
}

const getCtaLabelDefault = (resource: unknown): string => {
  const ctaLabel = (resource as { ctaLabel?: unknown } | null | undefined)
    ?.ctaLabel;
  if (typeof ctaLabel === "string") return ctaLabel;
  return getMultilingualString(
    (ctaLabel ?? {}) as Parameters<typeof getMultilingualString>[0],
  );
};

export const useAddEditResourceForm = ({
  onSuccess,
  onError,
  resource,
}: CreateEditResourceFormOptions) => {
  const defaultValues: CreateEditResourceFormValues = useMemo(
    () => ({
      type: resource?.type ?? EnumBroadcastResourcesType.Message,
      title: getMultilingualString(resource?.title ?? {}),
      description: getMultilingualString(resource?.description ?? {}),
      coverImage: resource?.coverImage?._id ?? "",
      message: getMultilingualString(resource?.message ?? {}),
      file: resource?.file?._id ?? "",
      video: resource?.video?._id ?? "",
      videoURL: resource?.videoURL ?? "",
      photos: resource?.photos?.map(p => p._id) ?? [],
      course: resource?.course ?? "",
      iframeUrl: resource?.iframeUrl ?? "",
      survey: resource?.survey?._id ?? "",
      formMode: (resource as any)?.formMode ?? "ONE_AND_DONE",
      allowSaveAndEdit: resource?.allowSaveAndEdit ?? false,
      requireApproval: (resource as any)?.requireApproval ?? false,
      allowResubmission: (resource as any)?.allowResubmission ?? false,
      completionTags: (resource as any)?.completionTags ?? [],
      submissionTags: (resource as any)?.submissionTags ?? [],
      booker: (resource as any)?.booker?._id ?? "",
      teamVisible: (resource as any)?.teamVisible ?? false,
      ctaLabel: getCtaLabelDefault(resource),
      _id: resource?._id ?? Date.now().toString(36),
    }),
    [resource],
  );
  const { trackPostHogException } = usePostHog();

  return useFormHandler<CreateEditResourceFormValues>(
    useCallback(
      data => {
        try {
          if (data) {
            onSuccess?.(data as CreateEditResourceFormValues);
          }
        } catch (error) {
          trackPostHogException(error, {
            fileName: "useAddEditResourceForm.ts",
            method: "onSubmit",
            message: "Error adding/editing resource",
          });
          onError?.(data?.type ?? "");
        }
      },
      [onError, onSuccess, trackPostHogException],
    ),
    defaultValues,
    { schema: createEditResourceFormSchema },
  );
};
